"use client";

import { forwardRef } from "react";
import { styled } from "@mui/material/styles";
import TextField, { type TextFieldOwnerState, type TextFieldProps } from "@mui/material/TextField";

/**
 * Styled text field for form funnel.
 * Matches the FunnelSelect aesthetic with consistent border, border-radius, and padding.
 */
const StyledFunnelTextField = styled(TextField)`
  .MuiInputLabel-root {
    position: static;
    transform: none;
    margin-bottom: 8px;
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 0.95rem;
    font-weight: 700;
    line-height: 1.4;
  }

  .MuiInputLabel-root.Mui-focused {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  .MuiOutlinedInput-root {
    border-radius: 8px;
    font-size: 1.1rem;
    min-height: 56px;
    background-color: ${({ theme }) => theme.palette.common.white};

    fieldset {
      border-width: 1px;
      border-color: ${({ theme }) => theme.palette.dream.borderColor};

      legend {
        width: 0;
        max-width: 0;
        padding: 0;
      }
    }

    &:hover fieldset {
      border-color: ${({ theme }) => theme.palette.primary.main};
    }

    &.Mui-focused fieldset {
      border-width: 2px;
      border-color: ${({ theme }) => theme.palette.primary.main};
    }

    &.Mui-disabled {
      background-color: ${({ theme }) => theme.palette.dream.grayLighter};

      fieldset {
        border-color: ${({ theme }) => theme.palette.dream.borderColor};
      }
    }
  }

  .MuiInputBase-input {
    box-sizing: border-box;
    min-height: 56px;
    padding: 16px;
  }

  &.-hasError .MuiOutlinedInput-root fieldset {
    border-color: ${({ theme }) => theme.palette.error.main};
  }
`;

export const FunnelTextField = forwardRef<HTMLDivElement, TextFieldProps>(function FunnelTextField(
  { slotProps, ...props },
  ref
) {
  const inputLabelSlotProps = slotProps?.inputLabel;

  return (
    <StyledFunnelTextField
      {...props}
      ref={ref}
      slotProps={{
        ...slotProps,
        inputLabel:
          typeof inputLabelSlotProps === "function"
            ? (ownerState: TextFieldOwnerState) => ({
                ...inputLabelSlotProps(ownerState),
                shrink: true,
              })
            : {
                ...inputLabelSlotProps,
                shrink: true,
              },
      }}
    />
  );
});
